import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  BadgeDollarSign,
  CalendarDays,
  CalendarRange,
  Car,
  Copy,
  Gauge,
  Moon,
  RefreshCcw,
  Share2,
  ShieldCheck,
  Sparkles,
  Sun,
  Wrench,
} from "lucide-react";
import { track } from "@vercel/analytics";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  formatDateForLocale,
  getMonthNames,
  localeOptions,
  type Locale,
  useI18n,
} from "@/lib/i18n";

type AgeBreakdown = {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  totalWeeks: number;
  birthDate: Date;
  referenceDate: Date;
};

const milestoneYears = [1, 3, 5, 10, 15, 25] as const;
const yearOptions = Array.from(
  { length: new Date().getFullYear() - 1900 + 1 },
  (_, index) => new Date().getFullYear() - index,
);
const monthOptions = Array.from({ length: 12 }, (_, index) => index + 1);
const adsenseClient = "ca-pub-7661439240252815";
const adsenseSlots = {
  test1: "7839452449",
  test2: "6137170146",
} as const;
const adsterraScripts = {
  banner: "b13c94dc83fc5b59dd1a154b68b3c05d",
  rectangle: "ac0d2f65ff686fc426db906c66b4ba3f",
  native: "09a09848e42558e99e069402f547aaf3",
} as const;

declare global {
  interface Window {
    adsbygoogle?: Array<Record<string, never>>;
  }
}

type AdsterraOptions = {
  key: string;
  format: "iframe";
  height: number;
  width: number;
  params: Record<string, never>;
};

const addYears = (date: Date, years: number) => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
};

const calculateAge = (
  manufacturingYear: string,
  manufacturingMonth: string,
  includeMonth: boolean,
): AgeBreakdown | null => {
  if (!manufacturingYear) return null;

  const year = Number(manufacturingYear);
  const month =
    includeMonth && manufacturingMonth ? Number(manufacturingMonth) : 1;
  if (!Number.isInteger(year) || !Number.isInteger(month)) return null;

  const birthDate = new Date(year, month - 1, 1, 12, 0, 0, 0);
  const referenceDate = new Date();
  referenceDate.setHours(12, 0, 0, 0);

  if (
    Number.isNaN(birthDate.getTime()) ||
    Number.isNaN(referenceDate.getTime())
  )
    return null;
  if (birthDate > referenceDate) return null;

  let years = referenceDate.getFullYear() - birthDate.getFullYear();
  let months = referenceDate.getMonth() - birthDate.getMonth();
  let days = referenceDate.getDate() - birthDate.getDate();

  if (days < 0) {
    const previousMonth = new Date(
      referenceDate.getFullYear(),
      referenceDate.getMonth(),
      0,
    );
    days += previousMonth.getDate();
    months -= 1;
  }

  if (months < 0) {
    months += 12;
    years -= 1;
  }

  const totalDays = Math.floor(
    (referenceDate.getTime() - birthDate.getTime()) / 86400000,
  );

  return {
    years,
    months,
    days,
    totalDays,
    totalWeeks: Math.floor(totalDays / 7),
    birthDate,
    referenceDate,
  };
};

const getAgeLabel = (
  years: number,
  labels: {
    almostNew: string;
    modern: string;
    mature: string;
    classic: string;
  },
) => {
  if (years < 3) return labels.almostNew;
  if (years < 8) return labels.modern;
  if (years < 15) return labels.mature;
  return labels.classic;
};

const getAgeEmoji = (years: number) => {
  if (years < 3) return "🚗";
  if (years < 8) return "🚙";
  if (years < 15) return "🚐";
  return "🏛️";
};

const getEstimatedValue = (
  years: number,
  values: {
    high: string;
    healthy: string;
    moderate: string;
    collector: string;
  },
) => {
  if (years < 3) return values.high;
  if (years < 8) return values.healthy;
  if (years < 15) return values.moderate;
  return values.collector;
};

const getMaintenanceStatus = (
  years: number,
  maintenance: {
    routine: string;
    watch: string;
    plan: string;
    specialized: string;
  },
) => {
  if (years < 3) return maintenance.routine;
  if (years < 8) return maintenance.watch;
  if (years < 15) return maintenance.plan;
  return maintenance.specialized;
};

const getRegistrationInfo = (
  years: number,
  registration: {
    collector: string;
    classic: string;
    standard: string;
    regular: string;
  },
) => {
  if (years >= 25) return registration.collector;
  if (years >= 15) return registration.classic;
  if (years >= 10) return registration.standard;
  return registration.regular;
};

const getNextMilestone = (birthDate: Date, referenceDate: Date) => {
  for (const years of milestoneYears) {
    const milestoneDate = addYears(birthDate, years);
    if (milestoneDate > referenceDate) {
      const remainingDays = Math.ceil(
        (milestoneDate.getTime() - referenceDate.getTime()) / 86400000,
      );
      return { years, remainingDays };
    }
  }

  return null;
};

// INP/FID Optimization: Debounced media query hook with passive listeners
const useMediaQuery = (query: string) => {
  const getMatches = () => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(getMatches);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    
    // Use requestAnimationFrame to batch updates and reduce main-thread blocking
    const handleChange = (event: MediaQueryListEvent) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setMatches(event.matches);
      });
    };

    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange, { passive: true });

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [query]);

  return matches;
};

type AdSenseUnitProps = {
  className?: string;
  format?: "auto" | "rectangle";
  onStatusChange?: (status: AdSenseStatus) => void;
  slot: string;
};

type AdSenseStatus = "pending" | "filled" | "unfilled" | "unfill-optimized";

const AdSenseUnit = ({
  className = "",
  format = "auto",
  onStatusChange,
  slot,
}: AdSenseUnitProps) => {
  const adRef = useRef<HTMLModElement | null>(null);
  const [adStatus, setAdStatus] = useState<AdSenseStatus>("pending");

  useEffect(() => {
    const adElement = adRef.current;
    if (!adElement) return;

    const updateAdStatus = () => {
      const nextStatus =
        (adElement.getAttribute("data-ad-status") as AdSenseStatus | null) ??
        "pending";

      setAdStatus(nextStatus);
      onStatusChange?.(nextStatus);
    };

    updateAdStatus();

    const observer = new MutationObserver(updateAdStatus);
    observer.observe(adElement, {
      attributeFilter: ["data-ad-status"],
      attributes: true,
    });

    return () => observer.disconnect();
  }, [onStatusChange]);

  useEffect(() => {
    try {
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.push({});
    } catch {
      // Ad blockers and local preview restrictions should not break the calculator.
    }
  }, []);

  return (
    <div
      className={`${adStatus === "unfilled" || adStatus === "unfill-optimized" ? "hidden" : ""} overflow-hidden ${className}`}
      aria-label="Advertisement"
    >
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={adsenseClient}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
};

type AdsterraIframeUnitProps = {
  className?: string;
  height: number;
  scriptKey: string;
  width: number;
};

const AdsterraIframeUnit = ({
  className = "",
  height,
  scriptKey,
  width,
}: AdsterraIframeUnitProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.replaceChildren();

    const options: AdsterraOptions = {
      key: scriptKey,
      format: "iframe",
      height,
      width,
      params: {},
    };
    const configScript = document.createElement("script");
    configScript.text = `window.atOptions = ${JSON.stringify(options)};`;

    const invokeScript = document.createElement("script");
    invokeScript.async = false;
    invokeScript.src = `https://pestilentberrysincere.com/${scriptKey}/invoke.js`;

    container.append(configScript, invokeScript);

    return () => container.replaceChildren();
  }, [height, scriptKey, width]);

  return (
    <div className={`flex justify-center overflow-hidden ${className}`} aria-label="Advertisement">
      <div ref={containerRef} style={{ maxWidth: "100%", width }} />
    </div>
  );
};

const AdsterraNativeUnit = ({ className = "" }: { className?: string }) => {
  const scriptHostRef = useRef<HTMLDivElement | null>(null);
  const containerId = `container-${adsterraScripts.native}`;

  useEffect(() => {
    const scriptHost = scriptHostRef.current;
    const adContainer = document.getElementById(containerId);
    if (!scriptHost || !adContainer) return;

    scriptHost.replaceChildren();
    adContainer.replaceChildren();

    const script = document.createElement("script");
    script.async = true;
    script.setAttribute("data-cfasync", "false");
    script.src = `https://pestilentberrysincere.com/${adsterraScripts.native}/invoke.js`;
    scriptHost.append(script);

    return () => {
      scriptHost.replaceChildren();
      adContainer.replaceChildren();
    };
  }, [containerId]);

  return (
    <div className={`overflow-hidden ${className}`} aria-label="Advertisement">
      <div id={containerId} />
      <div ref={scriptHostRef} />
    </div>
  );
};

const Index = () => {
  const { locale, setLocale, content } = useI18n();
  const [manufacturingYear, setManufacturingYear] = useState("");
  const [manufacturingMonth, setManufacturingMonth] = useState("");
  const [includeMonth, setIncludeMonth] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === "undefined") return true;

    const savedTheme = window.localStorage.getItem("theme");
    if (savedTheme) return savedTheme === "dark";

    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  // CLS Prevention: Track ad fill status to prevent layout shifts
  const [googleAdFilled, setGoogleAdFilled] = useState(false);
  const [googleAd2Filled, setGoogleAd2Filled] = useState(false);
  
  // Consistent breakpoints for mobile/desktop experience
  const hasDesktopAdRail = useMediaQuery("(min-width: 1280px)");
  const shouldShowInlineAd = useMediaQuery("(max-width: 1023px)");

  const age = useMemo(
    () => calculateAge(manufacturingYear, manufacturingMonth, includeMonth),
    [manufacturingYear, manufacturingMonth, includeMonth],
  );

  const nextMilestone = useMemo(() => {
    if (!age) return null;
    return getNextMilestone(age.birthDate, age.referenceDate);
  }, [age]);

  const monthNames = useMemo(() => getMonthNames(locale), [locale]);

  const selectedManufacturingLabel = useMemo(() => {
    if (!manufacturingYear) return "";

    if (includeMonth && manufacturingMonth) {
      const monthIndex = Number(manufacturingMonth) - 1;
      const monthName = monthNames[monthIndex] ?? monthNames[0];
      return `${monthName} ${manufacturingYear}`;
    }

    return manufacturingYear;
  }, [includeMonth, manufacturingMonth, manufacturingYear, monthNames]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    window.localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark((value) => !value);
    track("theme_toggled", { theme: isDark ? "light" : "dark" });
  };

  const switchLocale = (nextLocale: Locale) => {
    if (nextLocale === locale) return;
    setLocale(nextLocale);
    track("language_changed", { locale: nextLocale });
  };

  const resetForm = () => {
    setManufacturingYear("");
    setManufacturingMonth("");
    setIncludeMonth(false);
    track("calculator_reset");
    toast.message(content.resetMessage);
  };

  const handleIncludeMonthChange = (checked: boolean) => {
    setIncludeMonth(checked);

    if (checked && !manufacturingMonth) {
      setManufacturingMonth(String(currentMonth).padStart(2, "0"));
    }
  };

  const copySummary = async () => {
    if (!age) return;

    const summary = content.summary({
      label: selectedManufacturingLabel,
      years: age.years,
      months: age.months,
      days: age.days,
      asOf: formatDateForLocale(age.referenceDate, locale),
    });

    try {
      await navigator.clipboard.writeText(summary);
      track("summary_copied", {
        years: age.years,
        months: age.months,
      });
      toast.success(content.summaryCopied);
    } catch {
      toast.error(content.clipboardFailed);
    }
  };

  const shareSummary = async () => {
    if (!age) return;

    const summary = content.summary({
      label: selectedManufacturingLabel,
      years: age.years,
      months: age.months,
      days: age.days,
      asOf: formatDateForLocale(age.referenceDate, locale),
    });
    const sharePayload = {
      title: content.shareTitle,
      text: summary,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(sharePayload);
        track("summary_shared", { method: "native_share" });
        return;
      } catch {
        // Fall back to copy-only flow below.
      }
    }

    await copySummary();
    track("summary_shared", { method: "clipboard_fallback" });
  };

  return (
    <>
      <SEO
        title={content.seo.title}
        description={content.seo.description}
        keywords={content.seo.keywords}
        faq={content.seo.faq}
        canonicalUrl="/"
      />

      <div
        className={`min-h-screen overflow-hidden transition-colors duration-500 lg:h-screen ${
          isDark
            ? "bg-[radial-gradient(circle_at_top_left,_rgba(139,92,246,0.24),_transparent_32%),radial-gradient(circle_at_90%_10%,_rgba(59,130,246,0.18),_transparent_28%),linear-gradient(135deg,_#020617,_#0f172a_45%,_#111827)] text-white"
            : "bg-[radial-gradient(circle_at_top_left,_rgba(99,102,241,0.16),_transparent_32%),radial-gradient(circle_at_90%_10%,_rgba(14,165,233,0.14),_transparent_28%),linear-gradient(135deg,_#eff6ff,_#eef2ff_45%,_#f8fafc)] text-slate-900"
        }`}
      >
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div
            className={`absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full blur-3xl ${
              isDark ? "bg-fuchsia-500/10" : "bg-indigo-400/15"
            }`}
          />
          <div
            className={`absolute bottom-0 right-0 h-96 w-96 translate-x-1/3 translate-y-1/4 rounded-full blur-3xl ${
              isDark ? "bg-blue-500/10" : "bg-cyan-400/10"
            }`}
          />
        </div>

        <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-7xl flex-col px-4 py-5 sm:px-6 lg:h-full lg:min-h-0 lg:px-8 xl:max-w-[96rem]">
          <header className="mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`glass-panel flex h-12 w-12 items-center justify-center rounded-2xl border backdrop-blur-xl ${
                  isDark
                    ? "border-white/10 bg-white/10 shadow-[0_20px_60px_rgba(124,58,237,0.15)]"
                    : "border-white/60 bg-white/80 shadow-[0_20px_60px_rgba(99,102,241,0.12)]"
                }`}
              >
                <Car
                  className={
                    isDark ? "h-5 w-5 text-white" : "h-5 w-5 text-indigo-700"
                  }
                />
              </div>
              <div>
                <p
                  className={`text-xs uppercase tracking-[0.35em] ${isDark ? "text-purple-300" : "text-indigo-600"}`}
                >
                  {content.headerTagline}
                </p>
                <h1
                  className={`text-lg font-semibold sm:text-xl ${isDark ? "text-white" : "text-slate-900"}`}
                >
                  {content.headerTitle}
                </h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <label
                className={`glass-panel flex h-12 items-center gap-2 rounded-2xl border px-3 text-sm backdrop-blur-xl ${
                  isDark
                    ? "border-white/10 bg-white/5 text-white"
                    : "border-slate-200 bg-white/80 text-slate-700"
                }`}
              >
                <span className="sr-only">{content.languageLabel}</span>
                <Select
                  value={locale}
                  onValueChange={(value) => switchLocale(value as Locale)}
                >
                  <SelectTrigger
                    className={`min-w-[8.5rem] border-0 bg-transparent px-0 shadow-none focus:ring-0 focus:ring-offset-0 ${
                      isDark ? "text-white" : "text-slate-900"
                    }`}
                    aria-label={content.languageLabel}
                  >
                    <SelectValue placeholder={content.languageLabel} />
                  </SelectTrigger>
                  <SelectContent>
                    {localeOptions.map((item) => (
                      <SelectItem key={item.code} value={item.code}>
                        {item.nativeLabel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>

              <button
                type="button"
                onClick={resetForm}
                className={`glass-panel hidden items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium transition hover:-translate-y-0.5 sm:inline-flex ${
                  isDark
                    ? "border-white/10 bg-white/5 text-white hover:bg-white/10"
                    : "border-slate-200 bg-white/80 text-slate-700 hover:bg-white"
                }`}
              >
                <RefreshCcw className="h-4 w-4" />
                {content.reset}
              </button>

              <button
                type="button"
                onClick={toggleTheme}
                className={`glass-panel flex h-12 w-12 items-center justify-center rounded-2xl border backdrop-blur-xl transition hover:-translate-y-0.5 ${
                  isDark
                    ? "border-white/10 bg-white/10 text-white hover:bg-white/15"
                    : "border-slate-200 bg-white/80 text-slate-700 hover:bg-white"
                }`}
                aria-label={
                  isDark ? content.switchToLight : content.switchToDark
                }
              >
                {isDark ? (
                  <Sun className="h-5 w-5 text-amber-300" />
                ) : (
                  <Moon className="h-5 w-5 text-indigo-700" />
                )}
              </button>
            </div>
          </header>

          {/* CLS Prevention: Navbar ad banner with fixed height */}
          <div className="mb-4 w-full" style={{ minHeight: hasDesktopAdRail ? '90px' : '50px' }}>
            {hasDesktopAdRail ? (
              <div className="flex justify-center gap-4">
                <AdsterraIframeUnit
                  className="w-[728px]"
                  height={90}
                  scriptKey={adsterraScripts.banner}
                  width={728}
                />
              </div>
            ) : (
              <AdsterraIframeUnit
                className="w-full"
                height={50}
                scriptKey={adsterraScripts.banner}
                width={320}
              />
            )}
          </div>

          <main
            className={`relative grid flex-1 gap-6 lg:min-h-0 lg:grid-cols-[330px_minmax(0,1fr)] ${
              // Only show 3rd column when at least one Google ad is filled
              hasDesktopAdRail && (googleAdFilled || googleAd2Filled)
                ? "xl:grid-cols-[330px_minmax(0,1fr)_300px]"
                : "xl:grid-cols-[330px_minmax(0,1fr)]"
            }`}
          >
            {/* Left column: Stays visible */}
            <section className="space-y-4 lg:max-w-[330px]">
              <div
                className={`glass-panel overflow-hidden rounded-[2rem] border p-5 shadow-2xl backdrop-blur-xl sm:p-6 ${
                  isDark
                    ? "border-white/10 bg-white/6 shadow-black/30"
                    : "border-white/80 bg-white/85 shadow-indigo-200/40"
                }`}
              >
                <div className="space-y-3">
                  <p
                    className={`text-[10px] uppercase tracking-[0.35em] ${isDark ? "text-purple-300" : "text-indigo-600"}`}
                  >
                    {content.heroEyebrow}
                  </p>
                  <h2 className="text-2xl font-semibold leading-tight sm:text-[2rem]">
                    {content.heroTitle}
                  </h2>
                  <p
                    className={`text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}
                  >
                    {content.heroDescription}
                  </p>
                </div>
              </div>
              {/* CLS Prevention: Mobile inline ad with fixed height container */}
              {shouldShowInlineAd ? (
                <div style={{ minHeight: '50px' }} className="ad-container">
                  <AdsterraIframeUnit
                    className="-mx-4 w-[calc(100%+2rem)] sm:mx-auto sm:w-full"
                    height={50}
                    scriptKey={adsterraScripts.banner}
                    width={320}
                  />
                </div>
              ) : null}
              <div
                className={`glass-panel rounded-[2rem] border p-5 shadow-2xl backdrop-blur-xl sm:p-6 ${
                  isDark
                    ? "border-white/10 bg-white/7 shadow-black/20"
                    : "border-white/80 bg-white/90 shadow-indigo-200/40"
                }`}
              >
                <div className="mb-4 flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
                      isDark
                        ? "bg-purple-500/15 text-purple-300"
                        : "bg-indigo-50 text-indigo-700"
                    }`}
                  >
                    <CalendarDays className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold">
                      {content.inputTitle}
                    </h3>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label
                      className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}
                    >
                      {content.yearSelectLabel}
                    </label>
                    <div
                      className={`flex h-12 items-center rounded-2xl border px-4 ${
                        isDark
                          ? "border-white/10 bg-white/10"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <CalendarDays
                        className={`mr-3 h-4 w-4 ${isDark ? "text-purple-300" : "text-indigo-700"}`}
                      />
                      <Select
                        value={manufacturingYear}
                        onValueChange={(value) => {
                          setManufacturingYear(value);

                          if (
                            includeMonth &&
                            value === String(currentYear) &&
                            Number(manufacturingMonth) > currentMonth
                          ) {
                            setManufacturingMonth(
                              String(currentMonth).padStart(2, "0"),
                            );
                          }
                        }}
                      >
                        <SelectTrigger
                          className={`h-auto border-0 bg-transparent px-0 shadow-none focus:ring-0 focus:ring-offset-0 ${
                            isDark ? "text-white" : "text-slate-900"
                          }`}
                          aria-label={content.yearSelectLabel}
                        >
                          <SelectValue placeholder={content.yearPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {yearOptions.map((year) => (
                            <SelectItem key={year} value={String(year)}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <label
                    className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
                      isDark
                        ? "border-white/10 bg-white/5 text-slate-100"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={includeMonth}
                      onChange={(event) =>
                        handleIncludeMonthChange(event.target.checked)
                      }
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                    />
                    <span>
                      <span className="block font-medium">
                        {content.includeMonthLabel}
                      </span>
                      <span
                        className={`block text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}
                      >
                        {content.includeMonthNote}
                      </span>
                    </span>
                  </label>

                  {includeMonth ? (
                    <div className="space-y-2">
                      <label
                        className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}
                      >
                        {content.monthSelectLabel}
                      </label>
                      <div
                        className={`flex h-12 items-center rounded-2xl border px-4 ${
                          isDark
                            ? "border-white/10 bg-white/10"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <CalendarDays
                          className={`mr-3 h-4 w-4 ${isDark ? "text-purple-300" : "text-indigo-700"}`}
                        />
                        <Select
                          value={manufacturingMonth}
                          onValueChange={setManufacturingMonth}
                        >
                          <SelectTrigger
                            className={`h-auto border-0 bg-transparent px-0 shadow-none focus:ring-0 focus:ring-offset-0 ${
                              isDark ? "text-white" : "text-slate-900"
                            }`}
                            aria-label={content.monthSelectLabel}
                          >
                            <SelectValue
                              placeholder={content.monthPlaceholder}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {monthNames.map((monthName, index) => {
                              const monthValue = index + 1;
                              const isFutureMonth =
                                manufacturingYear === String(currentYear) &&
                                monthValue > currentMonth;

                              return (
                                <SelectItem
                                  key={monthName}
                                  value={String(monthValue).padStart(2, "0")}
                                  disabled={isFutureMonth}
                                >
                                  {monthName}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      isDark
                        ? "bg-white/10 text-slate-200"
                        : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {includeMonth
                      ? content.yearMonthBadge
                      : content.yearOnlyBadge}
                  </span>
                  <span
                    className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}
                  >
                    {content.blockedDateNote}
                  </span>
                </div>
              </div>
            </section>

            {/* Middle column */}
            <section className="space-y-6">
              {age ? (
                <>
                  <div
                    className={`glass-panel rounded-[2rem] border p-6 shadow-2xl backdrop-blur-xl sm:p-8 ${
                      isDark
                        ? "border-violet-400/25 bg-gradient-to-br from-violet-500/18 via-slate-900/80 to-blue-500/16 shadow-violet-950/30"
                        : "border-indigo-200/60 bg-gradient-to-br from-white via-indigo-50/80 to-cyan-50/70 shadow-indigo-200/50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p
                          className={`text-sm uppercase tracking-[0.3em] ${isDark ? "text-violet-200" : "text-indigo-600"}`}
                        >
                          {content.result}
                        </p>
                        <h3 className="mt-2 text-2xl font-semibold sm:text-3xl">
                          {getAgeEmoji(age.years)} {content.vehicleAgeLead}{" "}
                          {getAgeLabel(age.years, content.ageLabels)}
                        </h3>
                      </div>
                      <div
                        className={`rounded-2xl px-4 py-2 text-right ${
                          isDark
                            ? "bg-white/10 text-white"
                            : "bg-white/80 text-slate-800"
                        }`}
                      >
                        <p className="text-xs uppercase tracking-[0.24em] opacity-70">
                          {content.asOfToday}
                        </p>
                        <p className="text-sm font-semibold">
                          {formatDateForLocale(age.referenceDate, locale)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-8 grid gap-4 sm:grid-cols-3">
                      <div
                        className={`rounded-3xl border p-4 ${
                          isDark
                            ? "border-white/10 bg-white/7"
                            : "border-white/80 bg-white"
                        }`}
                      >
                        <p
                          className={`text-xs uppercase tracking-[0.24em] ${isDark ? "text-slate-400" : "text-slate-500"}`}
                        >
                          {content.years}
                        </p>
                        <p className="mt-2 text-3xl font-black">{age.years}</p>
                      </div>
                      <div
                        className={`rounded-3xl border p-4 ${
                          isDark
                            ? "border-white/10 bg-white/7"
                            : "border-white/80 bg-white"
                        }`}
                      >
                        <p
                          className={`text-xs uppercase tracking-[0.24em] ${isDark ? "text-slate-400" : "text-slate-500"}`}
                        >
                          {content.months}
                        </p>
                        <p className="mt-2 text-3xl font-black">{age.months}</p>
                      </div>
                      <div
                        className={`rounded-3xl border p-4 ${
                          isDark
                            ? "border-white/10 bg-white/7"
                            : "border-white/80 bg-white"
                        }`}
                      >
                        <p
                          className={`text-xs uppercase tracking-[0.24em] ${isDark ? "text-slate-400" : "text-slate-500"}`}
                        >
                          {content.days}
                        </p>
                        <p className="mt-2 text-3xl font-black">{age.days}</p>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                      <div
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm ${
                          isDark
                            ? "bg-white/10 text-slate-200"
                            : "bg-white text-slate-700"
                        }`}
                      >
                        <CalendarDays className="h-4 w-4" />
                        {selectedManufacturingLabel || content.yearOnlyBadge}
                      </div>
                      <div
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm ${
                          isDark
                            ? "bg-white/10 text-slate-200"
                            : "bg-white text-slate-700"
                        }`}
                      >
                        <Gauge className="h-4 w-4" />
                        {content.totalDays(age.totalDays)}
                      </div>
                      <div
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm ${
                          isDark
                            ? "bg-white/10 text-slate-200"
                            : "bg-white text-slate-700"
                        }`}
                      >
                        <BadgeDollarSign className="h-4 w-4" />
                        {getEstimatedValue(age.years, content.estimatedValue)}
                      </div>
                    </div>
                  </div>

                  <AdsterraNativeUnit className="w-full" />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div
                      className={`glass-panel rounded-[1.75rem] border p-5 backdrop-blur-xl ${
                        isDark
                          ? "border-white/10 bg-white/6"
                          : "border-white/80 bg-white/90"
                      }`}
                    >
                      <div className="mb-3 flex items-center gap-3">
                        <Wrench
                          className={`h-5 w-5 ${isDark ? "text-sky-300" : "text-sky-700"}`}
                        />
                        <h4 className="font-semibold">
                          {content.maintenanceTitle}
                        </h4>
                      </div>
                      <p
                        className={`text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}
                      >
                        {getMaintenanceStatus(age.years, content.maintenance)}
                      </p>
                    </div>

                    <div
                      className={`glass-panel rounded-[1.75rem] border p-5 backdrop-blur-xl ${
                        isDark
                          ? "border-white/10 bg-white/6"
                          : "border-white/80 bg-white/90"
                      }`}
                    >
                      <div className="mb-3 flex items-center gap-3">
                        <ShieldCheck
                          className={`h-5 w-5 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}
                        />
                        <h4 className="font-semibold">
                          {content.registrationTitle}
                        </h4>
                      </div>
                      <p
                        className={`text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}
                      >
                        {getRegistrationInfo(age.years, content.registration)}
                      </p>
                    </div>
                  </div>

                  <div
                    className={`glass-panel rounded-[1.75rem] border p-5 backdrop-blur-xl ${
                      isDark
                        ? "border-white/10 bg-white/6"
                        : "border-white/80 bg-white/90"
                    }`}
                  >
                    <div className="mb-4 flex items-center gap-3">
                      <ArrowRight
                        className={`h-5 w-5 ${isDark ? "text-purple-300" : "text-indigo-700"}`}
                      />
                      <h4 className="font-semibold">
                        {content.upcomingMilestoneTitle}
                      </h4>
                    </div>

                    {nextMilestone ? (
                      <div className="space-y-2">
                        <p
                          className={`text-xs uppercase tracking-[0.24em] ${isDark ? "text-slate-400" : "text-slate-500"}`}
                        >
                          {content.milestoneTimelineTitle}
                        </p>
                        <p className="text-lg font-semibold">
                          {content.milestoneRemaining({
                            label:
                              content.milestones[nextMilestone.years].label,
                            days: nextMilestone.remainingDays,
                          })}
                        </p>
                        <p
                          className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}
                        >
                          {content.milestones[nextMilestone.years].note}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-lg font-semibold">
                          {content.allMilestonesReachedTitle}
                        </p>
                        <p
                          className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}
                        >
                          {content.allMilestonesReachedBody}
                        </p>
                      </div>
                    )}
                  </div>

                  <div
                    className={`glass-panel rounded-[1.75rem] border p-5 backdrop-blur-xl ${
                      isDark
                        ? "border-white/10 bg-white/6"
                        : "border-white/80 bg-white/90"
                    }`}
                  >
                    <div className="mb-4 flex items-center gap-3">
                      <CalendarRange
                        className={`h-5 w-5 ${isDark ? "text-amber-300" : "text-amber-700"}`}
                      />
                      <h4 className="font-semibold">
                        {content.milestoneTimelineTitle}
                      </h4>
                    </div>

                    <div className="space-y-3">
                      {milestoneYears.map((years) => {
                        const milestone = content.milestones[years];
                        const milestoneDate = addYears(age.birthDate, years);
                        const reached = milestoneDate <= age.referenceDate;

                        return (
                          <div
                            key={years}
                            className={`flex items-start justify-between gap-4 rounded-2xl border px-4 py-3 ${
                              isDark
                                ? "border-white/10 bg-white/5"
                                : "border-slate-200 bg-white"
                            }`}
                          >
                            <div>
                              <p className="font-medium">{milestone.label}</p>
                              <p
                                className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}
                              >
                                {milestone.note}
                              </p>
                            </div>
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                reached
                                  ? isDark
                                    ? "bg-emerald-500/15 text-emerald-300"
                                    : "bg-emerald-50 text-emerald-700"
                                  : isDark
                                    ? "bg-white/10 text-slate-200"
                                    : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {reached ? content.reached : content.upcoming}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <button
                      type="button"
                      onClick={copySummary}
                      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium transition hover:-translate-y-0.5 ${
                        isDark
                          ? "bg-white/10 text-white hover:bg-white/15"
                          : "bg-slate-900 text-white hover:bg-slate-800"
                      }`}
                    >
                      <Copy className="h-4 w-4" />
                      {content.copySummary}
                    </button>

                    <button
                      type="button"
                      onClick={shareSummary}
                      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium transition hover:-translate-y-0.5 ${
                        isDark
                          ? "border border-white/10 bg-white/5 text-white hover:bg-white/10"
                          : "border border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                      }`}
                    >
                      <Share2 className="h-4 w-4" />
                      {content.shareResult}
                    </button>

                    <button
                      type="button"
                      onClick={resetForm}
                      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium transition hover:-translate-y-0.5 ${
                        isDark
                          ? "border border-white/10 bg-white/5 text-white hover:bg-white/10"
                          : "border border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                      }`}
                    >
                      <RefreshCcw className="h-4 w-4" />
                      {content.reset}
                    </button>
                  </div>
                </>
              ) : (
                <div
                  className={`glass-panel flex min-h-[680px] items-center justify-center rounded-[2rem] border p-8 text-center shadow-2xl backdrop-blur-xl ${
                    isDark
                      ? "border-white/10 bg-white/6 shadow-black/20"
                      : "border-white/80 bg-white/90 shadow-indigo-200/40"
                  }`}
                >
                  <div className="max-w-sm space-y-4">
                    <div
                      className={`mx-auto flex h-16 w-16 items-center justify-center rounded-3xl ${
                        isDark
                          ? "bg-purple-500/15 text-purple-300"
                          : "bg-indigo-50 text-indigo-700"
                      }`}
                    >
                      <Sparkles className="h-7 w-7" />
                    </div>
                    <h3 className="text-2xl font-semibold">
                      {content.emptyTitle}
                    </h3>
                    <p
                      className={`text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}
                    >
                      {content.emptyDescription}
                    </p>
                  </div>
                </div>
              )}
            </section>

            {/* 3rd column */}
            {hasDesktopAdRail && (googleAdFilled || googleAd2Filled) ? (
              <aside className="hidden xl:block">
                <div className="space-y-4">
                  {/* CLS Prevention: Fixed container sizes for ads */}
                  <div style={{ minHeight: '250px', width: '300px' }}>
                    <AdSenseUnit
                      className="w-[300px]"
                      format="rectangle"
                      slot={adsenseSlots.test1}
                      onStatusChange={(status) => setGoogleAdFilled(status === 'filled')}
                    />
                  </div>
                  <div style={{ minHeight: '250px', width: '300px' }}>
                    <AdSenseUnit
                      className="w-[300px]"
                      format="auto"
                      slot={adsenseSlots.test2}
                      onStatusChange={(status) => setGoogleAd2Filled(status === 'filled')}
                    />
                  </div>
                </div>
              </aside>
            ) : null}
          </main>
        </div>
      </div>
    </>
  );
};

export default Index;
